import { CanvassingTypes } from 'generated/graphql';

import {
  TAG_CATEGORY_COMBO_UI_CONDITION_FIELD, TAG_CATEGORY_RESPONSES_RULE_FIELD,
  TagCategoryResponsesRuleField
} from 'app/query-common/rule-interfaces/TagCategoryResponsesRuleField';
import { TagsAdvancedRuleField } from 'app/query-common/rule-interfaces/TagsAdvancedRuleField';
import { GOOD_STATUSES } from 'core/shared-constants';
import { isNilOrEmpty } from 'helpers';
import { Rule, RuleSet } from 'query-builder/query.interfaces';

/**
 * Contains default empty rules for the SimpleQueryBuilder
 */
export function defaultRuleset(
  defaultEntityTypeId?: string
): RuleSet {
  return {
    condition: 'and',
    rules: [
      {
        field: 'assessment_with_date',
        operator: 'include',
        value: {
          levels: null,
          created_before_date: null,
          created_after_date: null,
          relative_offset: null
        }
      },
      {
        field: 'name',
        operator: 'include',
        value: null
      },
      {
        field: 'entity_type',
        operator: 'in',
        value: defaultEntityTypeId ? [defaultEntityTypeId] : []
      },
      {
        field: 'follow_up_by',
        operator: '=',
        value: null
      },
      {
        field: 'is_user',
        operator: '=',
        value: null
      },
      {
        field: 'preferred_language',
        operator: 'in',
        value: []
      },
      {
        field: 'last_updated_offset',
        operator: null,
        value: null
      },
      {
        field: 'relationship_with',
        operator: 'in',
        value: []
      },
      {
        field: 'in_turf',
        operator: 'in',
        value: []
      },
      {
        field: 'email_simple',
        operator: '',
        value: {
          status: [],
          type: [],
          subscribed: false
        }
      },
      {
        field: 'address_simple',
        operator: '',
        value: {
          status: [],
          type: [],
          geocode_bad: null
        }
      },
      {
        field: 'social_simple',
        operator: 'includes',
        value: {
          status: [],
          type: []
        }
      },
      {
        field: 'entity_zip',
        operator: 'in',
        value: []
      },
      {
        field: 'entity_state',
        operator: 'in',
        value: []
      },
      {
        field: 'entity_city_state',
        operator: 'in',
        value: []
      },
      {
        field: 'entity_electoral_districts',
        operator: 'in',
        value: []
      },
      {
        field: 'phone_simple',
        operator: '',
        value: {
          status: [],
          type: [],
          subscribed: false
        }
      },
      {
        field: 'saved_query',
        operator: 'include',
        value: []
      },
      {
        condition: 'and',
        ui_condition_field: 'tags_combo',
        rules: [
          getDefaultTagsAdvancedRule('entity', 'include having any'),
          getDefaultTagsAdvancedRule('entity', 'exclude having any'),
        ]
      },
      getDefaultEntityConnectionSubqueryRule(),
    ]
  };
}


export function getDefaultTagCategoryResponsesRuleSet(
  entity: 'entity' | 'entity_connection' | 'calendar_event_response' = 'entity'
): RuleSet {
  return {
    condition: 'and',
    ui_condition_field: TAG_CATEGORY_COMBO_UI_CONDITION_FIELD,
    rules: [
      getDefaultTagCategoryResponsesRule(entity, 'include having any'),
      getDefaultTagCategoryResponsesRule(entity, 'exclude having any'),
    ]
  };
}

export function getDefaultTagCategoryResponsesRule(
  entity: 'entity' | 'entity_connection' | 'calendar_event_response',
  operator: 'include having any' | 'exclude having any'
): TagCategoryResponsesRuleField {
  return {
    field: TAG_CATEGORY_RESPONSES_RULE_FIELD,
    entity,
    operator,
    value: {
      tag_category_ids: []
    }
  };
}


export function getDefaultTagsComboRule(entity: 'entity' | 'entity_connection' | 'calendar_event_response' = 'entity'): RuleSet {
  return {
    condition: 'and',
    ui_condition_field: 'tags_combo',
    rules: [
      getDefaultTagsAdvancedRule(entity, 'include having any'),
      getDefaultTagsAdvancedRule(entity, 'exclude having any'),
    ]
  };
}

export function getDefaultTagsAdvancedRule(
  entity: 'entity' | 'entity_connection' | 'calendar_event_response',
  operator: 'include having any' | 'include having all' | 'exclude having any' | 'exclude having all'
): TagsAdvancedRuleField {
  return {
    field: 'tags_advanced',
    entity,
    operator,
    value: {
      tags: [],
      date_applied: null,
      createdByUsers: []
    }
  };
}

export function getDefaultEntityConnectionSubqueryRule() {
  return {
    field: 'entity_connection_subquery',
    operator: 'include',
    value: {
      include_inactive: false,
      subquery: {
        condition: 'and',
        rules: []
      },
      created_by: [],
      relative_offset: null,
      created_after_date: null,
      created_before_date: null
    }
  };
}

export function getDefaultCalendarEventResponseSubqueryRule() {
  return {
    field: 'calendar_event_response_subquery',
    operator: 'include',
    value: {
      subquery: {
        condition: 'and',
        rules: []
      },
      created_by: [],
      rsvp_after_date: null,
      rsvp_before_date: null,
      rsvp_relative_offset: null,
      calendar_event_ids: [],
      rsvp_statuses: [],
      min_calendar_event_responses: null,
      max_calendar_event_responses: null,
    }
  };
}

export function applyCanvassingDefaultQueryRules(ruleSet: RuleSet, canvassingType: CanvassingTypes) {
  if (isNilOrEmpty(canvassingType)) {
    return ruleSet;
  }

  if ([CanvassingTypes.PhoneBank, CanvassingTypes.TextBank].includes(canvassingType)) {
    const phoneSimpleRule: Rule = ruleSet.rules.find(r => (r as Rule).field === 'phone_simple') as Rule;

    phoneSimpleRule.operator = 'in';
    phoneSimpleRule.value = {
      status: GOOD_STATUSES,
      type: canvassingType === CanvassingTypes.TextBank ? ['cell'] : ['home', 'work', 'cell'],
      subscribed: false
    };
  }

  if ([CanvassingTypes.HomeVisit].includes(canvassingType)) {
    const rule: Rule = ruleSet.rules.find(r => (r as Rule).field === 'address_simple') as Rule;

    rule.operator = 'in';
    rule.value = {
      status: GOOD_STATUSES,
      mappable: true
    };
  }

  return ruleSet;
}
